import type {TFunction} from "i18next";
import {z} from "zod";
import {omegaGraphSchema} from "@/services/wingman/omega-results";
import {getCronDescription} from "@/components/workflows/utils/helpers/cronHelpers";
import {getTranslatedNodeDefinitionLabel, NODE_DEFINITIONS, nodeIsContainer} from "@/components/workflows/utils/helpers/workflow-node-registry";
import {humanizeToolField} from "@repo/ui/gailgpt/tool-presentation";
import {toolRequestBody} from "./toolRequestBody";
import {normalizeLocaleLanguage} from "@/constants/localization";

const publicationWorkflow = z.object({
   id: z.string().min(1).nullish(),
   name: z.string().trim().min(1),
   revision: z.union([z.number(), z.string()]).nullish(),
   graph: omegaGraphSchema,
});
export type PublicationWorkflow = z.infer<typeof publicationWorkflow>;

export function readPublicationWorkflow(parameters: Record<string, unknown>): PublicationWorkflow | null {
   const body = toolRequestBody(parameters);
   if (body.isDryRun === true) return null;
   const parsed = publicationWorkflow.safeParse(body);
   return parsed.success ? parsed.data : null;
}

function record(value: unknown): Record<string, unknown> {
   return value != null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

/** Only literal parameters can support specific claims in the publication summary. */
function literal(value: unknown): unknown {
   const parameter = record(value);
   return parameter.type === "literal" ? parameter.value : undefined;
}

type WorkflowNode = PublicationWorkflow["graph"]["nodes"][number];

export type PublicationStep = {
   id: string;
   name: string;
   definition: typeof NODE_DEFINITIONS[number] | undefined;
   type: ReturnType<typeof stepType>;
   children: PublicationStep[];
};

export function workflowPublicationSummary(workflow: PublicationWorkflow, t: TFunction<"ask">, wt: TFunction<"workflows">, language = "en") {
   const {triggers, cron, daily} = workflowTriggers(workflow, t, language);
   const connections = new Set<string>();
   if (cron) connections.add(t("publishWorkflow.scheduler"));
   const readSteps = (nodes: WorkflowNode[]): PublicationStep[] => nodes.map(node => {
      const definition = NODE_DEFINITIONS.find(item => item.type === node.type);
      const label = definition ? getTranslatedNodeDefinitionLabel(node.type, wt) : humanizeToolField(node.type);
      const connection = nodeConnection(node.type);
      if (connection) connections.add(connection);
      const nested = nodeIsContainer(node.type) ? omegaGraphSchema.safeParse(node.graph) : undefined;
      return {id: node.id, name: node.name || label, definition, type: stepType(node),
         children: nested?.success ? readSteps(nested.data.nodes) : []};
   });
   const steps = readSteps(workflow.graph.nodes);
   const allSteps = (items: PublicationStep[]): PublicationStep[] => items.flatMap(step => [step, ...allSteps(step.children)]);
   const flattened = allSteps(steps);
   const actions = workflow.graph.nodes.filter(node => stepType(node) === "action");
   const actionNames = flattened.filter(step => step.type === "action").map(step => step.name).join(", ");
   const summary = leadSearchSummary(actions, t, wt) ?? (actions.length
      ? t("publishWorkflow.actionsSummary", {actions: actionNames})
      : t("publishWorkflow.noActions"));
   let consequenceKey = "publishWorkflow.consequence";
   if (cron && triggers.length === 1) {
      consequenceKey = daily ? "publishWorkflow.consequenceDaily" : "publishWorkflow.consequenceSchedule";
   }
   return {steps, stepCount: flattened.length, connections: [...connections], runs: triggers.join(" · ") || t("publishWorkflow.onDemand"), summary,
      consequence: t(consequenceKey)};
}

function stepType(node: WorkflowNode) {
   if (node.type === "start" || node.type === "sub_graph_start") return "start";
   if (node.type === "end" || node.type === "sub_graph_end") return "finish";
   return "action";
}

function workflowTriggers(workflow: PublicationWorkflow, t: TFunction<"ask">, language: string) {
   const start = workflow.graph.nodes.find(node => node.type === "start");
   const parameters = record(start?.parameters);
   const schedule = record(start?.schedule ?? parameters.schedule);
   const webhook = record(start?.webhook ?? parameters.webhook);
   const metadata = record(start?.metadata ?? parameters.metadata);
   const cron = typeof schedule.cronExpression === "string" ? schedule.cronExpression.trim() : "";
   const daily = /^\d+\s+\d+\s+\*\s+\*\s+\*$/.test(cron);
   const triggers: string[] = [];
   if (cron) triggers.push(scheduleDescription(schedule, cron, daily, t, language));
   if (webhook.isEnabled === true) triggers.push(t("publishWorkflow.webhook"));
   if ((metadata.exposeAsTool ?? !!metadata.toolId) === true) triggers.push(t("publishWorkflow.assistantTool"));
   if ((record(start?.jotFormTrigger).isEnabled ?? metadata.jotformTrigger) === true) triggers.push(t("publishWorkflow.jotform"));
   if (record(start?.metaLeadGen).isEnabled === true) triggers.push(t("publishWorkflow.meta"));
   if (start?.embeddedForm) triggers.push(t("publishWorkflow.form"));
   const events = metadata.gailWebhookEvents;
   if (Array.isArray(events) && events.length) triggers.push(t("publishWorkflow.gailEvents"));
   return {triggers, cron, daily};
}

function scheduleDescription(schedule: Record<string, unknown>, cron: string, daily: boolean, t: TFunction<"ask">, language: string) {
   const [minute, hour] = cron.split(/\s+/).map(Number);
   let description = getCronDescription(cron, language) ?? cron;
   if (daily && hour < 24 && minute < 60) {
      const time = new Intl.DateTimeFormat(normalizeLocaleLanguage(language), {hour: "numeric", minute: "2-digit", timeZone: "UTC"})
         .format(new Date(Date.UTC(2020, 0, 1, hour, minute)));
      description = t("publishWorkflow.dailyAt", {time});
   }
   const zone = typeof schedule.timezone === "string" ? schedule.timezone : "UTC";
   return t("publishWorkflow.schedule", {schedule: description, timezone: zone === "America/New_York" ? t("publishWorkflow.eastern") : zone});
}

/** Describe the handoff's lead search from its actual config, never its free-text description. */
function leadSearchSummary(actions: WorkflowNode[], t: TFunction<"ask">, wt: TFunction<"workflows">) {
   if (actions.length !== 1 || actions[0].type !== "agencyzoom_search_leads") return undefined;
   const params = record(actions[0].parameters);
   const pageSize = literal(params.pageSize);
   const page = literal(params.page);
   const known = new Set(["page", "pageSize", "sort", "order", "status"]);
   const simple = Object.entries(params).every(([key, value]) => value == null || (known.has(key) && literal(value) !== undefined));
   if (!simple || typeof pageSize !== "number" || page !== 0) return undefined;
   const status = literal(params.status);
   let statusLabel = "";
   if (typeof status === "string" && status) {
      statusLabel = wt(`agencyZoomSearchLeadsSettings.status${status[0].toUpperCase()}${status.slice(1)}`, {defaultValue: humanizeToolField(status)});
   }
   let summary = t(status == null ? "publishWorkflow.leadsAll" : "publishWorkflow.leadsFiltered", {count: pageSize, status: statusLabel});
   if (literal(params.sort) === "lastActivityDate" && literal(params.order) === "desc") summary += t("publishWorkflow.recentActivity");
   return summary;
}

/** Service identity follows node capabilities, independently of the icon used to render them. */
function nodeConnection(type: string) {
   const services: Record<string, string> = {
      agencyzoom_: "AgencyZoom", momentum_: "Momentum", google_drive_: "Google Drive",
      google_sheets_: "Google Sheets", slack_: "Slack", jotform_: "Jotform",
   };
   return Object.entries(services).find(([prefix]) => type.startsWith(prefix))?.[1];
}
